export const formatCardNumber = (value: string): string => {
  const cleanedValue = value.replace(/\D/g, '').slice(0, 16)
  const parts = []

  for (let i = 0; i < cleanedValue.length; i += 4) {
    parts.push(cleanedValue.slice(i, i + 4))
  }

  return parts.join(' ')
}

export const formatExpirationDate = (value: string): string => {
  let inputValue = value.replace(/[^0-9]/g, '')
  if (inputValue.length >= 2) {
    inputValue = inputValue.substring(0, 2) + '/' + inputValue.substring(2, 4)
  }
  return inputValue
}

export const formatPhoneNumber = (value: string | null | undefined): string => {
  if (!value) {
    return ''
  }

  let numericValue = String(value).replace(/\D/g, '')

  if (numericValue.length === 11 && numericValue.startsWith('1')) {
    numericValue = numericValue.slice(1)
  }

  if (numericValue.length > 10) {
    numericValue = numericValue.slice(0, 10)
  }

  if (numericValue.length <= 3) {
    return numericValue
  } else if (numericValue.length <= 6) {
    return `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`
  } else {
    return `${numericValue.slice(0, 3)}-${numericValue.slice(
      3,
      6
    )}-${numericValue.slice(6, 10)}`
  }
}

export const formatCvv = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 4)
}

export const formatZipCode = (value: string): string => {
  let formattedZip = value.replace(/[^\d-]/g, '')

  if (formattedZip.length > 5 && !formattedZip.includes('-')) {
    formattedZip = `${formattedZip.slice(0, 5)}-${formattedZip.slice(5, 9)}`
  }

  return formattedZip
}

export const convertToFullDate = (mmYY: string) => {
  const [mm, yy] = mmYY.split('/')
  const fullYear = '20' + yy
  return `${fullYear}-${mm}`
}

export const removeSpaces = (input: string): string => {
  return input.replace(/\s+/g, '')
}

export const formatTaxId = (taxId: string): string => {
  const digits = taxId.replace(/\D/g, '')

  if (digits.length <= 2) return digits

  return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`
}

export const formatAveragePurchase = (value: string): string => {
  const digits = value.replace(/\D/g, '')

  const limitedDigits = digits.slice(0, 8)

  return `$${limitedDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

export const formatDisplayDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

export const validateCardNumber = (value: string): string => {
  const cleanValue = value.replace(/\s/g, '')

  if (!/^\d{16}$/.test(cleanValue)) {
    return 'Card Number must be exactly 16 digits.'
  }

  if (/^3[47]/.test(cleanValue)) {
    return 'AMEX cards are not accepted.'
  }

  if (
    /^6011/.test(cleanValue) ||
    /^65/.test(cleanValue) ||
    /^64[4-9]/.test(cleanValue) ||
    /^622(12[6-9]|1[3-9]\d|[2-8]\d\d|9[0-1]\d|92[0-5])/.test(cleanValue)
  ) {
    return 'Discover cards are not accepted.'
  }

  return ''
}

export const validateExpirationDate = (value: string): boolean => {
  return /^(0[1-9]|1[0-2])\/\d{2}$/.test(value)
}

export const validateCVV = (value: string): boolean => {
  return /^\d{3}$/.test(value)
}

export const validateZipCode = (value: string): boolean => {
  return /^\d{5}(-\d{4})?$/.test(value)
}

export const validatePhoneNumber = (value: string): boolean => {
  return /^\d{3}-\d{3}-\d{4}$/.test(value)
}

export const validateTaxId = (taxId: string): boolean => {
  const taxIdPattern = /^\d{2}-\d{7}$/
  return taxIdPattern.test(taxId)
}

export const validateAveragePurchase = (value: string): boolean => {
  const digits = value.replace(/\D/g, '')
  const pattern = /^\d{1,8}$/
  return pattern.test(digits)
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

export const validatePassword = (password: string): boolean => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/
  return regex.test(password)
}

export const validatePasswordComplexity = (password: string): string | null => {
  if (password.length < 8) {
    return 'Password must be at least 8 characters long.'
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must include at least one uppercase letter.'
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must include at least one lowercase letter.'
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must include at least one number.'
  }
  if (!/[!@#$%^&*]/.test(password)) {
    return 'Password must include at least one special character.'
  }
  return null
}

export const formatToTitleCase = (text: string): string => {
  return text
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const roundToTwo = (num: number) => Math.round(num * 100) / 100
